<template>
  <div class="foods-qr-list">
    <page-header
      :title="'QR - ' + toLatin(restaurantName)"
      :subtitle="$t('qr_codes_list_meals')"
      icon="mdi-qrcode-scan"
      icon-color="primary"
    >
      <template #actions>
        <v-btn text color="primary" :to="{ name: 'jela' }">
          <v-icon class="mr-2">mdi-arrow-left</v-icon>
          {{ $t("back_to_meals") }}
        </v-btn>
      </template>
    </page-header>

    <foods-qr-toolbar :foods="currentItems" @cardUpdated="card = $event" />

    <div class="px-6 mt-2">
      <v-data-table
        id="qrTable"
        :headers="headers"
        :items="foods"
        class="ordera-box-shadow striped-table"
        mobile-breakpoint="0"
        :loading="loading"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        hide-default-footer
        @current-items="currentItems = $event"
        @page-count="pageCount = $event"
      >
        <!-- Image Column -->
        <template v-slot:item.image="{ item }">
          <div class="d-flex align-center justify-center py-1">
            <v-icon v-if="!item.image" style="font-size: 40px" color="grey lighten-1">
              mdi-image-off
            </v-icon>
            <v-img
              v-else-if="item.image && item.image.name === 'no-image.png'"
              :src="item.image.url"
              height="40"
              width="40"
              contain
            />
            <v-img
              v-else-if="item.image && conf && restaurantId"
              :src="`${conf.apiUrlBase}/gallery/${restaurantId}/medium/${item.image.name}`"
              height="40"
              width="40"
              contain
            />
          </div>
        </template>

        <!-- Name Column -->
        <template v-slot:item.name="{ item }">
          {{ toLatin(item.name) }}
        </template>

        <!-- Category Column -->
        <template v-slot:item.category="{ item }">
          <v-chip v-if="item.category" small outlined>
            {{ toLatin(item.category.name) }}
          </v-chip>
        </template>

        <!-- QR Code Column -->
        <template v-slot:item.qrCode="{ item }">
          <div class="d-flex justify-center pt-2 pb-1">
            <div class="qr-code-container" :title="$t('click_to_print')">
              <qrcode-vue
                :id="`qrkodstampa${item.id}`"
                :value="item.qrCode"
                :size="120"
                level="H"
                render-as="svg"
                class="qr-code"
              />
            </div>
          </div>
        </template>
      </v-data-table>

      <v-row align="center" justify="end" no-gutters class="mt-4">
        <text-field
          :value="itemsPerPage"
          :label="$t('meals_per_page')"
          type="number"
          :min="1"
          @input="itemsPerPage = parseInt($event, 10)"
        ></text-field>
        <v-pagination class="ml-4" v-model="page" :length="pageCount"></v-pagination>
      </v-row>
    </div>
  </div>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import FoodsQrToolbar from "@/components/qr/FoodsQrToolbar.vue";
import restaurantService from "@/services/restaurant";
import categoryService from "@/services/category";
import hasSnackbar from "@/mixins/hasSnackbar";
import conf from "@/config";

export default {
  name: "FoodsQrList",
  mixins: [hasSnackbar],
  components: {
    QrcodeVue,
    PageHeader,
    FoodsQrToolbar,
  },
  data: () => ({
    restaurantName: "",
    foods: [],
    loading: false,
    conf: conf,
    page: 1,
    pageCount: 0,
    itemsPerPage: 10,
    currentItems: [],
    card: {},
  }),
  created() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const restaurantId = this.$store.state.restaurant?.id;
        if (!restaurantId) {
          this.showErrorSnackbar(this.$t("error_no_restaurant"));
          return;
        }

        const [categoriesRes, foodsRes] = await Promise.all([
          categoryService.getCategories(),
          restaurantService.getRestaurantFoods(restaurantId),
        ]);

        const categoriesMap = {};
        (categoriesRes.data || []).forEach((cat) => {
          categoriesMap[cat.id] = cat;
        });

        this.restaurantName = foodsRes.data.name;

        this.foods = (foodsRes.data.foods || []).map((food) => ({
          ...food,
          category: categoriesMap[food.category_id] || null,
          qrCode: food.qrCode || JSON.stringify({ type: "food", id: food.id }),
        }));
      } catch (error) {
        console.error("Error loading data:", error);
        this.showErrorSnackbar(this.$t("error_loading_meals"));
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },
    headers() {
      return [
        { text: "Id", value: "id" },
        { text: this.$t("image"), value: "image", sortable: false },
        { text: this.$t("code"), value: "code" },
        { text: this.$t("name"), value: "name" },
        { text: this.$t("group"), value: "category" },
        { text: this.$t("qr_code"), value: "qrCode", sortable: false, align: "center" },
      ];
    },
  },
};
</script>

